import type { User } from '@/types/schemas/api'

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID as string | undefined

declare global {
  interface Window {
    Intercom: (...args: unknown[]) => void
    intercomSettings: Record<string, unknown>
  }
}

let scriptLoaded = false

function loadIntercomScript(appId: string) {
  if (scriptLoaded) return
  scriptLoaded = true

  const w = window
  const ic = w.Intercom
  if (typeof ic === 'function') {
    ic('reattach_activator')
    ic('update', w.intercomSettings)
  } else {
    const queue: unknown[][] = []
    const i = Object.assign(
      (...args: unknown[]) => {
        queue.push(args)
      },
      {
        q: queue,
        c: (args: unknown[]) => {
          queue.push(args)
        },
      },
    )
    w.Intercom = i as unknown as typeof w.Intercom

    const s = document.createElement('script')
    s.type = 'text/javascript'
    s.async = true
    s.src = `https://widget.intercom.io/widget/${appId}`
    document.head.appendChild(s)
  }
}

/**
 * `email` comes from the auth session (the profile schema does not carry it). Legacy
 * booted with it too, so an existing contact keeps its email in the inbox.
 */
export function bootIntercom(user: User, email?: string) {
  if (!INTERCOM_APP_ID) return

  loadIntercomScript(INTERCOM_APP_ID)

  window.Intercom('boot', {
    app_id: INTERCOM_APP_ID,
    // Identity hash (ihmac) is computed server-side over edgeUserId, so user_id
    // must match it. edgeUserId is a hard invariant on the profile response.
    user_id: user.edgeUserId,
    email: email || undefined,
    name: [user.givenName, user.familyName].filter(Boolean).join(' ') || undefined,
    user_hash: user.ihmac ?? undefined,
    hide_default_launcher: true,
  })
}

export function showIntercom() {
  if (typeof window.Intercom === 'function') {
    window.Intercom('show')
  }
}

export function shutdownIntercom() {
  if (!INTERCOM_APP_ID) return
  if (typeof window.Intercom === 'function') {
    window.Intercom('shutdown')
  }
}
